import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CONFIG from '../config';

const CURRENCIES = ['USDINR','EURINR','GBPINR','AEDINR','SGDINR','AUDINR'];
const num = (v) => parseFloat(v) || 0;
const inr = (v) => '₹ ' + Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fc  = (v) => Number(v || 0).toLocaleString('en-IN');

function PcfcPage({ token }) {
  const auth = { headers: { authorization: `Bearer ${token}` } };

  const [loans, setLoans]   = useState([]);
  const [banks, setBanks]   = useState([]);
  const [loading, setLoading] = useState(true);

  // ---------- Opening ----------
  const [opening, setOpening] = useState({
    bank: '', currency: 'USDINR', pcfcDate: '', fcAmount: '',
    conversionRate: '', inrValue: '', status: 'OPEN'
  });
  const [openingExists, setOpeningExists]   = useState(false);
  const [openingEditing, setOpeningEditing] = useState(false);

  // ---------- New loan form ----------
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    pcfcRef: '', pcfcDate: '', bank: '', currency: 'USDINR',
    fcAmount: '', notes: ''
  });
  const [spot, setSpot] = useState({ fcAmount: '', exRate: '' });
  const [fwdLines, setFwdLines] = useState([]);
  const [availableForwards, setAvailableForwards] = useState([]);
  const [importBills, setImportBills] = useState([]);
  const [importSel, setImportSel] = useState({});

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [l, b] = await Promise.all([
        axios.get(`${CONFIG.API}/api/bills/pcfc`, auth),
        axios.get(`${CONFIG.API}/api/bills/banks`, auth)
      ]);
      setLoans(l.data);
      setBanks(b.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadOpening = async (bank, currency) => {
    if (!bank || !currency) return;
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/pcfc/opening?bank=${encodeURIComponent(bank)}&currency=${currency}`,
        auth
      );
      if (res.data) {
        setOpening({
          bank, currency,
          pcfcDate:       res.data.pcfcDate || '',
          fcAmount:       res.data.fcAmount != null ? String(res.data.fcAmount) : '',
          conversionRate: res.data.drawdownRate != null ? String(res.data.drawdownRate) : '',
          inrValue:       res.data.inrReceived != null ? String(res.data.inrReceived) : '',
          status:         res.data.status || 'OPEN'
        });
        setOpeningExists(true);
      } else {
        setOpening(o => ({ ...o, bank, currency, pcfcDate: '', fcAmount: '', conversionRate: '', inrValue: '', status: 'OPEN' }));
        setOpeningExists(false);
      }
      setOpeningEditing(false);
    } catch (err) { console.error(err); }
  };

  const handleOpeningChange = (e) => {
    const { name, value } = e.target;
    const next = { ...opening, [name]: value };
    if (name === 'fcAmount' || name === 'conversionRate') {
      const f = num(name === 'fcAmount' ? value : next.fcAmount);
      const r = num(name === 'conversionRate' ? value : next.conversionRate);
      if (f && r) next.inrValue = (f * r).toFixed(2);
    }
    setOpening(next);
    if (name === 'bank' || name === 'currency') {
      loadOpening(name === 'bank' ? value : next.bank, name === 'currency' ? value : next.currency);
    }
  };

  const saveOpening = async () => {
    if (!opening.bank)           { alert('Pick a bank for the opening'); return; }
    if (!opening.pcfcDate)       { alert('Enter the opening date'); return; }
    if (!opening.fcAmount)       { alert('Enter the opening FC amount'); return; }
    if (!opening.conversionRate) { alert('Enter the conversion rate'); return; }
    try {
      await axios.post(`${CONFIG.API}/api/bills/pcfc/opening`, {
        bank: opening.bank, currency: opening.currency,
        pcfcDate: opening.pcfcDate,
        fcAmount: num(opening.fcAmount),
        conversionRate: num(opening.conversionRate),
        inrValue: opening.inrValue ? num(opening.inrValue) : null,
        status: opening.status
      }, auth);
      setOpeningExists(true);
      setOpeningEditing(false);
      loadAll();
    } catch (err) {
      alert(err.response?.data || 'Error saving opening.');
    }
  };

  const onFormCurrency = async (currency) => {
    try {
      const [fwd, imp] = await Promise.all([
        axios.get(`${CONFIG.API}/api/bills/forwards/available/${currency}`, auth),
        axios.get(`${CONFIG.API}/api/bills/import-orders`, auth)
      ]);
      setAvailableForwards(fwd.data);
      const bills = imp.data
        .filter(b => b.currency === currency && num(b.fcBalance) > 0)
        .sort((a, b) => (a.expectedPaymentDate || '').localeCompare(b.expectedPaymentDate || ''));
      setImportBills(bills);
      setImportSel({});
    } catch (err) { console.error(err); }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (name === 'currency') onFormCurrency(value);
  };

  const openNewForm = () => {
    setForm({ pcfcRef: '', pcfcDate: '', bank: '', currency: 'USDINR', fcAmount: '', notes: '' });
    setSpot({ fcAmount: '', exRate: '' });
    setFwdLines([]);
    setImportSel({});
    setShowForm(true);
    onFormCurrency('USDINR');
  };

  const addFwdLine = () => setFwdLines([...fwdLines, { forwardId: '', fcAmount: '', exRate: '' }]);
  const removeFwdLine = (i) => setFwdLines(fwdLines.filter((_, idx) => idx !== i));
  const changeFwdLine = (i, field, value) => {
    const next = [...fwdLines];
    next[i][field] = value;
    if (field === 'forwardId') {
      const f = availableForwards.find(x => String(x.forwardId) === String(value));
      next[i].exRate = f ? f.forwardRate : '';
    }
    setFwdLines(next);
  };

  const toggleImport = (bill) => {
    setImportSel(prev => {
      const next = { ...prev };
      if (next[bill.importOrderId]) delete next[bill.importOrderId];
      else next[bill.importOrderId] = { fc: '', rate: bill.costRate };
      return next;
    });
  };
  const changeImportFc = (bill, value) => {
    setImportSel(prev => ({
      ...prev,
      [bill.importOrderId]: { ...prev[bill.importOrderId], fc: value, rate: bill.costRate }
    }));
  };

  // live totals for the form
  const spotFc = num(spot.fcAmount);
  const fwdFc  = fwdLines.reduce((s, l) => s + num(l.fcAmount), 0);
  const impFc  = Object.values(importSel).reduce((s, x) => s + num(x.fc), 0);
  const allocated = spotFc + fwdFc + impFc;
  const loanFc = num(form.fcAmount);
  const balanced = loanFc > 0 && Math.abs(allocated - loanFc) < 0.01;
  const inrTotal =
    spotFc * num(spot.exRate) +
    fwdLines.reduce((s, l) => s + num(l.fcAmount) * num(l.exRate), 0) +
    Object.values(importSel).reduce((s, x) => s + num(x.fc) * num(x.rate), 0);
  const weightedRate = allocated > 0 ? (inrTotal / allocated) : 0;

  const saveLoan = async () => {
    if (!form.pcfcRef)  { alert('Enter PCFC ref'); return; }
    if (!form.pcfcDate) { alert('Enter transaction date'); return; }
    if (!form.bank)     { alert('Pick a bank'); return; }
    if (!loanFc)        { alert('Enter loan amount'); return; }

    const conversions = [];
    if (spotFc > 0) {
      if (!spot.exRate) { alert('Enter the spot ex rate'); return; }
      conversions.push({ method: 'SPOT', fcAmount: spotFc, exRate: num(spot.exRate) });
    }
    for (const l of fwdLines) {
      const f = num(l.fcAmount);
      if (f <= 0) continue;
      if (!l.forwardId) { alert('Pick a forward for each forward line'); return; }
      conversions.push({ method: 'FORWARD', fcAmount: f, exRate: num(l.exRate), linkedForwardId: parseInt(l.forwardId) });
    }
    for (const bill of importBills) {
      const sel = importSel[bill.importOrderId];
      if (!sel) continue;
      const f = num(sel.fc);
      if (f <= 0) continue;
      if (f > num(bill.fcBalance) + 0.01) {
        alert(`Pay FC for ${bill.sellerCode} (${bill.orderNo}) exceeds its balance of ${bill.fcBalance}`); return;
      }
      conversions.push({ method: 'IMPORT', fcAmount: f, exRate: num(bill.costRate), linkedImportOrderId: bill.importOrderId });
    }

    if (conversions.length === 0) { alert('Add at least one conversion (spot, forward or import).'); return; }
    if (!balanced) { alert(`Conversion total ${allocated} must equal loan amount ${loanFc}.`); return; }

    try {
      await axios.post(`${CONFIG.API}/api/bills/pcfc`, {
        pcfcRef: form.pcfcRef, pcfcDate: form.pcfcDate, bank: form.bank,
        currency: form.currency, fcAmount: loanFc, notes: form.notes || null,
        conversions
      }, auth);
      setShowForm(false);
      loadAll();
    } catch (err) {
      alert(err.response?.data || 'Error saving PCFC.');
    }
  };

  const cancelLoan = async (loan) => {
    if (!window.confirm(`Cancel ${loan.pcfcRef}?`)) return;
    try {
      await axios.put(`${CONFIG.API}/api/bills/pcfc/${loan.pcfcId}/cancel`, {}, auth);
      loadAll();
    } catch (err) {
      alert(err.response?.data || 'Error cancelling.');
    }
  };

  const badge = (s) => ({
    OPEN: 'badge-open', PART_REPAID: 'badge-partial',
    FULLY_REPAID: 'badge-done', CANCELLED: 'badge-cancelled'
  }[s] || 'badge-open');

  const activeBanks = banks.filter(b => b.status === 'ACTIVE');

  // ----- summary card figures -----
  const openings    = loans.filter(l => l.entryType === 'OPENING');
  const loanEntries = loans.filter(l => l.entryType !== 'OPENING');

  const openFcT  = openings.reduce((s, l) => s + num(l.fcAmount), 0);
  const openInrT = openings.reduce((s, l) => s + num(l.inrReceived), 0);
  const drawnFcT  = loanEntries.reduce((s, l) => s + num(l.fcAmount), 0);
  const drawnInrT = loanEntries.reduce((s, l) => s + num(l.inrReceived), 0);
  const repaidFcT  = loans.reduce((s, l) => s + num(l.fcRepaid), 0);
  const repaidInrT = 0; // repayment-rate based; populated once repayment step is built
  const outFcT  = openFcT + drawnFcT - repaidFcT;
  const outInrT = openInrT + drawnInrT - repaidInrT;

  const Card = ({ label, fcVal, inrVal, accent, filled }) => (
    <div style={{
      flex: 1, borderRadius: '12px', padding: '14px 16px',
      background: filled ? '#1F4E79' : '#fff',
      border: filled ? 'none' : '1px solid #E2E8F0',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
    }}>
      <div style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '.03em',
        textTransform: 'uppercase', color: filled ? '#BFDBFE' : '#94A3B8', marginBottom: '8px' }}>
        {label}
      </div>
      <div style={{ fontSize: '22px', fontWeight: 700, color: filled ? '#fff' : (accent || '#1E293B'), lineHeight: 1.1 }}>
        {fcVal}
      </div>
      <div style={{ fontSize: '13px', marginTop: '4px', color: filled ? '#DBEAFE' : '#64748B' }}>
        {inrVal}
      </div>
    </div>
  );

  const lock = !openingEditing;

  return (
    <div className="content">

      {/* ---------- Summary cards ---------- */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '22px', flexWrap: 'wrap' }}>
        <Card label="Open (opening)" fcVal={fc(openFcT)}  inrVal={inr(openInrT)}  accent="#1D4ED8" />
        <Card label="Drawn (loans)"  fcVal={fc(drawnFcT)} inrVal={inr(drawnInrT)} accent="#0F766E" />
        <Card label="Repaid"         fcVal={fc(repaidFcT)} inrVal={inr(repaidInrT)} accent="#B45309" />
        <Card label="Outstanding"    fcVal={fc(outFcT)}   inrVal={inr(outInrT)}  filled />
      </div>

      {/* ---------- Opening card ---------- */}
      <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px',
        padding: '16px 18px', marginBottom: '22px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '14px' }}>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#1E293B' }}>
            🏁 Opening PCFC balance <span style={{ fontWeight: 400, color: '#94A3B8', fontSize: '13px' }}>— per bank</span>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            {!openingEditing && (
              <button className="btn-secondary" onClick={() => setOpeningEditing(true)} disabled={!opening.bank}>
                {openingExists ? '✏️ Edit' : '+ Set opening'}
              </button>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
          <div className="form-field">
            <label>Bank</label>
            <select name="bank" value={opening.bank} onChange={handleOpeningChange} disabled={openingEditing}>
              <option value="">Select bank</option>
              {activeBanks.map(b => <option key={b.bankId} value={b.bankName}>{b.bankName}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label>Currency</label>
            <select name="currency" value={opening.currency} onChange={handleOpeningChange} disabled={openingEditing}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label>Date</label>
            <input type="date" name="pcfcDate" value={opening.pcfcDate} onChange={handleOpeningChange} disabled={lock}
              style={lock ? { background: '#F8FAFC' } : {}} />
          </div>
          <div className="form-field">
            <label>FC amount</label>
            <input type="number" name="fcAmount" value={opening.fcAmount} onChange={handleOpeningChange} disabled={lock}
              placeholder="0" style={lock ? { background: '#F8FAFC' } : {}} />
          </div>
          <div className="form-field">
            <label>Conversion rate</label>
            <input type="number" name="conversionRate" value={opening.conversionRate} onChange={handleOpeningChange} disabled={lock}
              placeholder="e.g. 83.75" style={lock ? { background: '#F8FAFC' } : {}} />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', marginTop: '12px' }}>
          <div className="form-field" style={{ flex: '0 0 240px' }}>
            <label>INR value (optional, auto)</label>
            <input type="number" name="inrValue" value={opening.inrValue} onChange={handleOpeningChange} disabled={lock}
              placeholder="auto = FC × rate" style={lock ? { background: '#F8FAFC' } : {}} />
          </div>
          {openingEditing && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
              <button className="btn-secondary" onClick={() => loadOpening(opening.bank, opening.currency)}>Cancel</button>
              <button className="btn-primary" onClick={saveOpening}>
                {openingExists ? '✓ Update opening' : '✓ Save opening'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ---------- Header ---------- */}
      <div className="section-header">
        <div className="section-title">PCFC loans</div>
        <button className="btn-primary" onClick={() => showForm ? setShowForm(false) : openNewForm()}>
          {showForm ? '✕ Close' : '+ New PCFC'}
        </button>
      </div>

      {/* ---------- New loan form ---------- */}
      {showForm && (
        <div className="form-card" style={{ maxWidth: '860px' }}>
          <div className="form-step" style={{ marginBottom: '16px' }}>📋 New PCFC loan</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div className="form-field"><label>PCFC ref</label>
              <input name="pcfcRef" value={form.pcfcRef} onChange={handleFormChange} placeholder="Bank loan ref" /></div>
            <div className="form-field"><label>Transaction date</label>
              <input type="date" name="pcfcDate" value={form.pcfcDate} onChange={handleFormChange} /></div>
            <div className="form-field"><label>Bank</label>
              <select name="bank" value={form.bank} onChange={handleFormChange}>
                <option value="">Select bank</option>
                {activeBanks.map(b => <option key={b.bankId} value={b.bankName}>{b.bankName}</option>)}
              </select></div>
            <div className="form-field"><label>Currency</label>
              <select name="currency" value={form.currency} onChange={handleFormChange}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select></div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gap: '12px', marginBottom: '18px' }}>
            <div className="form-field"><label>Loan amount (FC)</label>
              <input type="number" name="fcAmount" value={form.fcAmount} onChange={handleFormChange} placeholder="0" /></div>
            <div className="form-field"><label>Notes (optional)</label>
              <input name="notes" value={form.notes} onChange={handleFormChange} placeholder="Any notes..." /></div>
          </div>

          <div style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '10px' }}>
            Conversion split — how this loan converts to INR
          </div>

          {/* SPOT */}
          <div style={{ border: '1px solid #E2E8F0', borderRadius: '10px', padding: '12px 14px', marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748B', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '.03em' }}>Spot</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '10px' }}>
              <div className="form-field"><label>FC amount</label>
                <input type="number" value={spot.fcAmount} onChange={e => setSpot({ ...spot, fcAmount: e.target.value })} placeholder="0" /></div>
              <div className="form-field"><label>Ex rate (you type)</label>
                <input type="number" value={spot.exRate} onChange={e => setSpot({ ...spot, exRate: e.target.value })} placeholder="e.g. 84.90" /></div>
              <div></div>
            </div>
          </div>

          {/* FORWARD */}
          <div style={{ border: '1px solid #E2E8F0', borderRadius: '10px', padding: '12px 14px', marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748B', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '.03em' }}>Forward</div>
            {fwdLines.map((l, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 32px', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
                <select value={l.forwardId} onChange={e => changeFwdLine(i, 'forwardId', e.target.value)}>
                  <option value="">Select forward</option>
                  {availableForwards.map(f => (
                    <option key={f.forwardId} value={f.forwardId}>
                      {f.dealNo} · avail {fc(f.fcAvailable)} · {Number(f.forwardRate).toFixed(4)}
                    </option>
                  ))}
                </select>
                <input type="number" value={l.fcAmount} onChange={e => changeFwdLine(i, 'fcAmount', e.target.value)} placeholder="FC amount" />
                <input value={l.exRate ? Number(l.exRate).toFixed(4) : ''} disabled style={{ background: '#F1F5F9' }} />
                <button className="btn-secondary" onClick={() => removeFwdLine(i)} style={{ padding: '6px 8px' }}>✕</button>
              </div>
            ))}
            <button className="btn-secondary" onClick={addFwdLine}>+ Add forward</button>
          </div>

          {/* IMPORT */}
          <div style={{ border: '1px solid #E2E8F0', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748B', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '.03em' }}>
              Import bills <span style={{ fontWeight: 400, color: '#94A3B8', textTransform: 'none' }}>· tick to pay · rate auto-fills · ordered by due date</span>
            </div>
            {importBills.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#94A3B8', padding: '6px' }}>No open import bills in this currency.</div>
            ) : (
              <table className="data-table" style={{ fontSize: '13px' }}>
                <thead><tr>
                  <th></th><th>Party</th><th>Order no</th><th>Bill amt</th><th>Balance</th><th>Pay FC</th><th>Rate</th>
                </tr></thead>
                <tbody>
                  {importBills.map(bill => {
                    const sel = importSel[bill.importOrderId];
                    return (
                      <tr key={bill.importOrderId}>
                        <td><input type="checkbox" checked={!!sel} onChange={() => toggleImport(bill)} /></td>
                        <td>{bill.sellerCode}</td>
                        <td>{bill.orderNo}</td>
                        <td>{fc(bill.fcAmount)}</td>
                        <td>{fc(bill.fcBalance)}</td>
                        <td>
                          <input type="number" disabled={!sel} value={sel ? sel.fc : ''}
                            onChange={e => changeImportFc(bill, e.target.value)}
                            style={{ width: '90px', background: sel ? '#fff' : '#F1F5F9' }} />
                        </td>
                        <td>{Number(bill.costRate).toFixed(4)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Balance + weighted rate */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
            <div style={{ flex: 1, borderRadius: '8px', padding: '10px 14px', fontSize: '13px',
              background: balanced ? '#F0FDF4' : '#FEF2F2',
              color: balanced ? '#166534' : '#DC2626',
              border: `1px solid ${balanced ? '#BBF7D0' : '#FCA5A5'}` }}>
              {balanced ? '✓ ' : '⚠ '}Allocated {fc(allocated)} / {fc(loanFc)}
              {balanced ? ' — balanced' : ' — must match loan amount'}
            </div>
            <div style={{ flex: 1, background: '#F1F5F9', borderRadius: '8px', padding: '10px 14px', fontSize: '13px' }}>
              Weighted ex rate: <strong>{weightedRate ? weightedRate.toFixed(4) : '—'}</strong>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="btn-primary" onClick={saveLoan}>✓ Save PCFC</button>
          </div>
        </div>
      )}

      {/* ---------- Table ---------- */}
      <div className="table-wrap" style={{ marginTop: '16px' }}>
        {loading ? (
          <p style={{ padding: '20px', color: '#64748B' }}>Loading...</p>
        ) : loans.length === 0 ? (
          <p style={{ padding: '20px', color: '#64748B' }}>No PCFC entries yet.</p>
        ) : (
          <table className="data-table">
            <thead><tr>
              <th>Date</th><th>Bank</th><th>Ref</th><th>Ccy</th><th>Loan FC</th>
              <th>Ex rate</th><th>INR received</th><th>Repaid</th><th>Available</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {loans.map(l => (
                <tr key={l.pcfcId} style={l.entryType === 'OPENING' ? { background: '#F8FAFC' } : {}}>
                  <td>{l.pcfcDate}</td>
                  <td>{l.bank}{l.entryType === 'OPENING' && <em style={{ color: '#94A3B8' }}> · Opening</em>}</td>
                  <td>{l.pcfcRef}</td>
                  <td>{l.currency}</td>
                  <td>{fc(l.fcAmount)}</td>
                  <td>{l.drawdownRate != null ? Number(l.drawdownRate).toFixed(4) : '—'}</td>
                  <td>{l.inrReceived != null ? fc(l.inrReceived) : '—'}</td>
                  <td>{fc(l.fcRepaid)}</td>
                  <td style={{ color: '#166534' }}>{fc(l.fcAvailable)}</td>
                  <td><span className={`badge ${badge(l.status)}`}>{l.status}</span></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {num(l.fcRepaid) === 0 && (
                      <button className="btn-danger" onClick={() => cancelLoan(l)}>Cancel</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}

export default PcfcPage;